import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { useSelector, useStore } from "react-redux";

import NumberFormat from "react-number-format";
import { Alert, Checkbox, FormControlLabel, Radio, RadioGroup, Slide } from "@mui/material";

import { InputField } from "../components/InputField.tsx";
import { useConfig } from "../hooks/useConfig.ts";
import {
  setInvestmentSummary,
  setTotals,
} from "../Store/roiStore.ts";
import { Total } from "../components/Total.tsx";
import { getTotal as getTotalUtil } from "../utils/index.ts";
import { PAR_SOLUTIONS, hasPar360Solution } from "../constants.ts";
import {
  CUSTOMER_SITUATION,
  SKU_REDUCTION_KEY,
  isExistingCustomerMode,
  resolveCustomerSituation,
  sumFacilityScales,
} from "../utils/customerSituation.ts";
import "../styles/pages/investment-summary.scss";

const SCALE_SOLUTION = "PAR Scales";
const ESL_SOLUTION = "Electronic Shelf Labels (ESLs)";
const PAR360_PREMIUM = "PAR 360 Premium";
// Freight / "Package Delivery" consolidation inputs, unlocked by PAR 360 Premium.
const FREIGHT_SPEND_KEY = "Annual Freight Spend";
const FREIGHT_SAVINGS_PCT_KEY = "Freight Savings %";

export const InvestmentSummary = () => {
    const config = useConfig();
    const configData = config.inputFields.investmentSummary;
    const dataInStore = useSelector(
        (state) => state.inputFields.investmentSummary
    );
    // PAR fees are entered as annual amounts for new ROIs. Legacy ROIs (opened from
    // a save that predates this change) keep their original monthly entry + ×12
    // semantics so their numbers still reconcile. The store key names are cadence-
    // specific so legacy and annual values never collide.
    const isLegacyRoi = useSelector((state) => !!state.inputFields.isLegacyRoi);
    const cadenceWord = isLegacyRoi ? "Monthly" : "Annual";
    const periodsPerYear = isLegacyRoi ? 12 : 1;
    const eslDefault = isLegacyRoi ? "0.31" : "3.72";
    const subscriptionKey = (solution: string) =>
        `${solution} ${cadenceWord} Subscription Fee`;
    const facilitySubKey = (solution: string, rowKey: string) =>
        `${subscriptionKey(solution)}|${rowKey}`;
    const applyAllKey = (solution: string) =>
        `${subscriptionKey(solution)}|applyToAll`;
    const spendDetailRows = useSelector(
        (state) => state.inputFields.spendDetailsRows
    );
    const selectedSolutionsRaw: string[] = useSelector(
        (state) => state.inputFields.client?.currentParSolutions ?? []
    );
    const client = useSelector((state) => state.inputFields.client);
    const situation = resolveCustomerSituation(client);
    const existingMode = isExistingCustomerMode(situation);
    const par360Selected = hasPar360Solution(selectedSolutionsRaw);
    const scaleSplitTotals = sumFacilityScales(spendDetailRows, situation);
    const billableScaleCount = existingMode
        ? scaleSplitTotals.newScales
        : getTotalUtil("Scale Count", spendDetailRows);
    const selectedSolutions = useMemo(
        () => selectedSolutionsRaw
            .filter((s) => PAR_SOLUTIONS.includes(s))
            .sort((a, b) => PAR_SOLUTIONS.indexOf(a) - PAR_SOLUTIONS.indexOf(b)),
        [selectedSolutionsRaw]
    );
    // PAR 360 Premium carries no subscription fee of its own — it reuses the PAR 360
    // Plus fee and only unlocks the Bags Delivery freight inputs.
    // Incremental add-on analyses also drop PAR Scales: existing scale subscription
    // is already in the customer's run-rate and is not a new cost of this deal.
    const feeSolutions = useMemo(
        () => selectedSolutions.filter((s) =>
            s !== PAR360_PREMIUM && !(situation === CUSTOMER_SITUATION.PAR360_ADDON && s === SCALE_SOLUTION)
        ),
        [selectedSolutions, situation]
    );
    const showFreight = selectedSolutions.includes(PAR360_PREMIUM);
    const [investmentTotal, setInvestmentTotal] = useState(0);
    const { dispatch } = useStore();

    const rowKeys = useMemo(
        () => Object.keys(spendDetailRows).filter(k => k.startsWith("row")),
        [spendDetailRows]
    );
    const facilityCount = rowKeys.length;

    const getTotal = () => {
        const upfrontKeys = configData
            .filter((item) => item.includeInUpfrontTotal)
            .map((item) => item.customText);
        const total = upfrontKeys
            .map((item) => parseFloat(dataInStore[item] ?? 0) ?? 0)
            .reduce((a, b) => a + b, 0);
        return total;
    };

    useEffect(() => {
        dispatch(
            setTotals({
                name: "investment",
                value: investmentTotal,
            })
        );
    }, [dispatch, investmentTotal]);

    useEffect(() => {
        if (dataInStore && Object.keys(dataInStore).length) {
            setInvestmentTotal(getTotal());
        }
    }, [dataInStore]);

    // --- Annual Recurring Support Fees calculation + per-solution map ---
    useEffect(() => {
        const totalScales = billableScaleCount;
        const totalSkus = getTotalUtil("Total SKUs", spendDetailRows);

        let annualRecurring = 0;
        const perSolutionMap: Record<string, number> = {};

        for (const solution of feeSolutions) {
            let solutionAnnual = 0;
            if (solution === SCALE_SOLUTION) {
                const scaleRate = parseFloat(dataInStore[subscriptionKey(SCALE_SOLUTION)] ?? 0);
                solutionAnnual = scaleRate * totalScales * periodsPerYear;
            } else if (solution === ESL_SOLUTION) {
                const eslRate = parseFloat(dataInStore[subscriptionKey(ESL_SOLUTION)] ?? eslDefault);
                solutionAnnual = eslRate * totalSkus * periodsPerYear;
            } else {
                const isApplyAll = !!dataInStore[applyAllKey(solution)];
                if (isApplyAll) {
                    const systemRate = parseFloat(dataInStore[subscriptionKey(solution)] ?? 0);
                    solutionAnnual = systemRate * facilityCount * periodsPerYear;
                } else {
                    const perFacilityTotal = rowKeys.reduce((sum, rk) =>
                        sum + parseFloat(dataInStore[facilitySubKey(solution, rk)] ?? 0), 0);
                    solutionAnnual = perFacilityTotal * periodsPerYear;
                }
            }
            perSolutionMap[solution] = solutionAnnual;
            annualRecurring += solutionAnnual;
        }

        dispatch(
            setInvestmentSummary({
                name: "Annual Recurring Support Fees (est.)",
                value: annualRecurring.toFixed(2),
            })
        );
        dispatch(
            setInvestmentSummary({
                name: "solutionSubscriptions",
                value: JSON.stringify(perSolutionMap),
            })
        );
        // Stamp new ROIs as annual-priced so they're never misread as legacy on
        // reload. Guarded so we don't dispatch (and re-render) on every keystroke.
        if (!isLegacyRoi && dataInStore["pricingModel"] !== "annual") {
            dispatch(
                setInvestmentSummary({ name: "pricingModel", value: "annual" })
            );
        }
    }, [dispatch, spendDetailRows, feeSolutions, dataInStore, facilityCount, rowKeys, periodsPerYear, eslDefault, isLegacyRoi, billableScaleCount]);

    const renderFreightSection = () => {
        if (!showFreight) return null;

        return (
            <>
                <div className="investment-summary-label investment-summary-section-header">
                    Package Delivery (Freight Consolidation)
                </div>
                <div />

                <div className="investment-summary-label" style={{ alignSelf: "start", paddingTop: "10px" }}>
                    Annual Freight Spend
                </div>
                <InputField
                    value={dataInStore?.[FREIGHT_SPEND_KEY]}
                    onValueInputChange={(name, value) =>
                        dispatch(setInvestmentSummary({
                            name: FREIGHT_SPEND_KEY,
                            value: value,
                        }))
                    }
                    hiddenLabel
                    prefix="$"
                    type="number"
                    defaultValue="0"
                    helperText="Customer's total annual freight cost."
                />

                <div className="investment-summary-label" style={{ alignSelf: "start", paddingTop: "10px" }}>
                    Estimated Freight Savings %
                </div>
                <InputField
                    value={dataInStore?.[FREIGHT_SAVINGS_PCT_KEY]}
                    onValueInputChange={(name, value) =>
                        dispatch(setInvestmentSummary({
                            name: FREIGHT_SAVINGS_PCT_KEY,
                            value: value,
                        }))
                    }
                    hiddenLabel
                    suffix="%"
                    type="number"
                    defaultValue="0"
                    placeholder="10"
                    helperText="Agreed savings from package consolidation (typically 5–15%)."
                />
            </>
        );
    };

    const renderSubscriptionSection = () => {
        if (feeSolutions.length === 0) return null;

        return (
            <>
                <div className="investment-summary-label investment-summary-section-header">
                    Subscription Fees
                </div>
                <div />

                {feeSolutions.map((solution) => {
                    if (solution === SCALE_SOLUTION) {
                        return (
                            <React.Fragment key={solution}>
                                <div className="investment-summary-label" style={{ alignSelf: "start", paddingTop: "10px" }}>
                                    PAR Scales {cadenceWord} Subscription Rate (per scale)
                                </div>
                                <InputField
                                    value={dataInStore?.[subscriptionKey(SCALE_SOLUTION)]}
                                    onValueInputChange={(name, value) =>
                                        dispatch(setInvestmentSummary({
                                            name: subscriptionKey(SCALE_SOLUTION),
                                            value: value,
                                        }))
                                    }
                                    hiddenLabel
                                    prefix="$"
                                    suffix={isLegacyRoi ? "/scale/mo" : "/scale/yr"}
                                    type="number"
                                    defaultValue="0"
                                    placeholder={isLegacyRoi ? "3.00" : "36.00"}
                                    helperText={existingMode
                                        ? `Per-scale ${cadenceWord.toLowerCase()} rate. Applied against new scales in this deal (${billableScaleCount || 0}), not the installed base.`
                                        : `Per-scale ${cadenceWord.toLowerCase()} rate. Applied against the total Scale Count from Spend Details.`}
                                />
                            </React.Fragment>
                        );
                    }
                    if (solution === ESL_SOLUTION) {
                        return (
                            <React.Fragment key={solution}>
                                <div className="investment-summary-label" style={{ alignSelf: "start", paddingTop: "10px" }}>
                                    ESL {cadenceWord} Cost per Tag (all facilities)
                                </div>
                                <InputField
                                    value={dataInStore?.[subscriptionKey(ESL_SOLUTION)] ?? eslDefault}
                                    onValueInputChange={(name, value) =>
                                        dispatch(setInvestmentSummary({
                                            name: subscriptionKey(ESL_SOLUTION),
                                            value: value,
                                        }))
                                    }
                                    hiddenLabel
                                    prefix="$"
                                    suffix={isLegacyRoi ? "/mo" : "/yr"}
                                    type="number"
                                    defaultValue={eslDefault}
                                    helperText="Applied against total SKUs from Spend Details as a proxy for tag count."
                                />
                            </React.Fragment>
                        );
                    }

                    const isApplyAll = !!dataInStore[applyAllKey(solution)];

                    return (
                        <React.Fragment key={solution}>
                            <div className="investment-summary-label">
                                {solution} {cadenceWord} Subscription Fee
                            </div>
                            <div className="investment-summary-apply-all">
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            size="small"
                                            checked={isApplyAll}
                                            onChange={(e) =>
                                                dispatch(setInvestmentSummary({
                                                    name: applyAllKey(solution),
                                                    value: e.target.checked ? "1" : "",
                                                }))
                                            }
                                        />
                                    }
                                    label="Apply to all facilities"
                                />
                            </div>

                            {isApplyAll ? (
                                <React.Fragment>
                                    <div className="investment-summary-label investment-summary-facility-label">
                                        All facilities
                                    </div>
                                    <InputField
                                        value={dataInStore?.[subscriptionKey(solution)]}
                                        onValueInputChange={(name, value) =>
                                            dispatch(setInvestmentSummary({
                                                name: subscriptionKey(solution),
                                                value: value,
                                            }))
                                        }
                                        hiddenLabel
                                        prefix="$"
                                        type="number"
                                        defaultValue="0"
                                    />
                                </React.Fragment>
                            ) : (
                                rowKeys.map((rowKey, i) => {
                                    const facilityName = spendDetailRows[rowKey]?.["Facility Name"] || `Facility ${i + 1}`;
                                    const storeKey = facilitySubKey(solution, rowKey);
                                    return (
                                        <React.Fragment key={storeKey}>
                                            <div className="investment-summary-label investment-summary-facility-label">
                                                {facilityName}
                                            </div>
                                            <InputField
                                                value={dataInStore?.[storeKey]}
                                                onValueInputChange={(name, value) =>
                                                    dispatch(setInvestmentSummary({
                                                        name: storeKey,
                                                        value: value,
                                                    }))
                                                }
                                                hiddenLabel
                                                prefix="$"
                                                type="number"
                                                defaultValue="0"
                                            />
                                        </React.Fragment>
                                    );
                                })
                            )}
                        </React.Fragment>
                    );
                })}
            </>
        );
    };

    return (
        <Slide direction="right" in={true} mountOnEnter unmountOnExit>
            <div className="investment-summary-page">
                <h1>PAR Excellence Investment</h1>
                <p>Please enter Investment Summary below.</p>
                {situation === CUSTOMER_SITUATION.PAR360_ADDON && (
                    <Alert severity="info" sx={{ width: "80%", margin: "0 auto 16px", textAlign: "left" }}>
                        Incremental PAR 360 analysis: existing scale subscription is excluded.
                        Enter the existing-customer PAR 360 rate (typically discounted vs net-new)
                        and any PAR 360 implementation / onboarding fees only.
                    </Alert>
                )}
                {(situation === CUSTOMER_SITUATION.MIXED || situation === CUSTOMER_SITUATION.SCALE_EXPANSION) && (
                    <Alert severity="info" sx={{ width: "80%", margin: "0 auto 16px", textAlign: "left" }}>
                        Scale subscription is applied only to new scales in this deal
                        ({billableScaleCount || 0}). Put hardware purchases in Scale hardware
                        (CAPEX) — Year 1 only. Enter the existing-customer PAR 360 rate for
                        the system.
                    </Alert>
                )}
                <div>
                    <div className="investment-summary-grid">
                        {renderSubscriptionSection()}
                        {renderFreightSection()}

                        {configData.map((currentCell, index) => (
                            <React.Fragment key={currentCell.customText}>
                                <div className="investment-summary-label">{currentCell.displayText ?? currentCell.customText}</div>
                                <InputField
                                    key={index}
                                    value={dataInStore?.[currentCell.customText]}
                                    onValueInputChange={(name, value) =>
                                        dispatch(
                                            setInvestmentSummary({
                                                name: currentCell?.customText ?? "",
                                                value: value,
                                            })
                                        )
                                    }
                                    {...(currentCell.hiddenLabel && {
                                        hiddenLabel: currentCell.hiddenLabel,
                                    })}
                                    {...(currentCell.prefix && { prefix: currentCell.prefix })}
                                    {...(currentCell.suffix && { suffix: currentCell.suffix })}
                                    {...(currentCell.type && { type: currentCell.type })}
                                    {...(currentCell.disabled && {
                                        disabled: currentCell.disabled,
                                    })}
                                    {...(currentCell.defaultValue && {
                                        defaultValue: currentCell.defaultValue,
                                    })}
                                />
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                <div className="investment-summary-options-box">
                    <div className="options-box-title">
                        Overstock Recovery Period
                    </div>
                    <RadioGroup
                        row
                        value={dataInStore?.["overstockRecoveryYears"] || "3"}
                        onChange={(e) =>
                            dispatch(setInvestmentSummary({
                                name: "overstockRecoveryYears",
                                value: e.target.value,
                            }))
                        }
                    >
                        <FormControlLabel value="1" control={<Radio size="small" />} label="1 Year" />
                        <FormControlLabel value="2" control={<Radio size="small" />} label="2 Years" />
                        <FormControlLabel value="3" control={<Radio size="small" />} label="3 Years" />
                    </RadioGroup>
                </div>

                {par360Selected && (
                    <div className="investment-summary-options-box">
                        <div className="options-box-title">
                            PAR 360 SKU reduction (optional)
                        </div>
                        <p style={{ margin: "0 0 10px", fontSize: "0.85rem", color: "#555" }}>
                            Share of addressable spend removed by eliminating true non-mover
                            overstock. Leave 0 if you are not using this line.
                        </p>
                        <InputField
                            label="SKU reduction from non-mover overstock"
                            value={dataInStore?.[SKU_REDUCTION_KEY] ?? 0}
                            suffix="%"
                            type="number"
                            defaultValue="0"
                            onValueInputChange={(_name, value) =>
                                dispatch(setInvestmentSummary({
                                    name: SKU_REDUCTION_KEY,
                                    value: value,
                                }))
                            }
                        />
                    </div>
                )}

                <div className="investment-summary-options-box">
                    <div className="options-box-title">
                        PDF Report Options
                    </div>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dataInStore?.["showLaborSavingsDetail"] !== ""}
                                onChange={(e) =>
                                    dispatch(setInvestmentSummary({
                                        name: "showLaborSavingsDetail",
                                        value: e.target.checked ? "1" : "",
                                    }))
                                }
                            />
                        }
                        label="Show labor savings breakdown (Daily Scanning & Call Down) on PDF"
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={dataInStore?.["showDelayCostOnPdf"] !== ""}
                                onChange={(e) =>
                                    dispatch(setInvestmentSummary({
                                        name: "showDelayCostOnPdf",
                                        value: e.target.checked ? "1" : "",
                                    }))
                                }
                            />
                        }
                        label="Show Delay Cost Per Day on PDF (when opportunity ≥ $5M)"
                    />

                    <div className="options-box-group">
                        <div className="options-box-group-label">Service Pricing Format</div>
                        <RadioGroup
                            value={dataInStore?.["servicePricingDisplay"] || "aggregate"}
                            onChange={(e) =>
                                dispatch(setInvestmentSummary({
                                    name: "servicePricingDisplay",
                                    value: e.target.value,
                                }))
                            }
                        >
                            <FormControlLabel value="aggregate" control={<Radio size="small" />} label="Show combined total" />
                            <FormControlLabel value="bySolution" control={<Radio size="small" />} label="Show breakdown per solution" />
                        </RadioGroup>
                    </div>
                </div>

                <Total
                    total={{
                        title: "Total Upfront Investment",
                        value: (
                            <NumberFormat
                                allowEmptyFormatting
                                value={investmentTotal}
                                displayType={"text"}
                                thousandSeparator={true}
                                prefix={"$"}
                            />
                        ),
                    }}
                />

                <div className="investment-summary-note">
                    *include direct charges, cancellation fees, etc., not captured
                    elsewhere
                </div>
            </div>
        </Slide>
    );
};
